import { useState, useEffect } from 'react';

/**
 * Custom hook for tracking scroll position and active section
 * 
 * @param {Array<string>} sectionIds - Array of section IDs to track
 * @param {Object} options - Configuration options
 * @param {number} options.offset - Offset from the top for section detection
 * @param {number} options.threshold - Threshold for considering a section active (0-1)
 * @returns {Object} Scroll tracking information
 * @returns {string} activeSection - ID of the currently active section
 * @returns {boolean} isScrolled - Whether the page has been scrolled down
 * @returns {number} scrollY - Current scroll position
 * @returns {number} scrollDirection - Scroll direction (1 for down, -1 for up, 0 for no movement)
 */
const useScrollTracking = (sectionIds = [], options = {}) => {
  const { offset = 100, threshold = 0.3, scrollThreshold = 50 } = options;

  const [activeSection, setActiveSection] = useState("");
  const [isScrolled, setIsScrolled] = useState(false); // Start as false
  const [scrollY, setScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Update scroll position
      setScrollY(currentScrollY);

      // Determine if page is scrolled
      setIsScrolled(currentScrollY > scrollThreshold);

      // Determine scroll direction
      if (currentScrollY > lastScrollY) {
        setScrollDirection(1); // scrolling down
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection(-1); // scrolling up
      }
      setLastScrollY(currentScrollY);

      // Find active section
      if (sectionIds.length > 0) {
        // Get all sections
        const sections = sectionIds
          .map((id) => {
            const element = document.getElementById(id);
            if (!element) return null;

            const rect = element.getBoundingClientRect();
            return {
              id,
              top: rect.top,
              bottom: rect.bottom,
              height: rect.height,
            };
          })
          .filter(Boolean);

        // Find the active section
        let current = "";

        // First check if any section is taking a significant portion of the viewport
        const viewportHeight = window.innerHeight;
        const significantSection = sections.find((section) => {
          const visibleHeight =
            Math.min(section.bottom, viewportHeight) - Math.max(section.top, 0);
          return (
            visibleHeight > 0 && visibleHeight / viewportHeight > threshold
          );
        });

        if (significantSection) {
          current = significantSection.id;
        } else {
          // Otherwise, find the first section that's near the top of the viewport
          for (const section of sections) {
            if (section.top <= offset) {
              current = section.id;
            } else {
              break;
            }
          }
        }

        setActiveSection(current);
      }
    };

    // Initial check
    handleScroll();

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sectionIds, offset, threshold, scrollThreshold, lastScrollY]);

  return { activeSection, isScrolled, scrollY, scrollDirection };
};

export default useScrollTracking;